import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getListProjectsQueryKey } from "~/api/folders/folders";
import { useArchiveProject, useGetProject } from "~/api/projects/projects";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

interface ArchiveCaseAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
}

export const ArchiveCaseAlertDialog: FC<ArchiveCaseAlertDialogProps> = ({
  open,
  setOpen,
  projectId,
}) => {
  const { organizationId } = useOrganizationParams();
  const { data } = useGetProject(organizationId, projectId);
  const { mutateAsync: archiveProject } = useArchiveProject();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();
  const [isFinalizing, setIsFinalizing] = useState(false);

  const onCancel = () => {
    trackEvent({ eventName: "app/archive_case_modal/cancel.button/click" });
    setOpen(false);
  };

  const onArchiveCase = async () => {
    trackEvent({ eventName: "app/archive_case_modal/archive.button/submit" });
    try {
      setIsFinalizing(true);
      await archiveProject({ organizationId, projectId });
      await queryClient.invalidateQueries({
        queryKey: getListProjectsQueryKey(organizationId),
      });
      // Ensure the list query finishes refetching before closing
      await queryClient.refetchQueries({
        queryKey: getListProjectsQueryKey(organizationId, { archived: false }),
      });
      await queryClient.refetchQueries({
        queryKey: getListProjectsQueryKey(organizationId, { archived: true }),
      });
      setOpen(false);
    } catch (error: any) {
      const isTimeout =
        error?.code === "ECONNABORTED" || error?.response?.status === 504;
      toast({
        title: intl.formatMessage({ id: "error", defaultMessage: "Error" }),
        description: isTimeout
          ? intl.formatMessage({
              id: "archiveCase.timeout",
              defaultMessage:
                "Request timed out. The case may still be archiving. Please check later or try again.",
            })
          : intl.formatMessage({
              id: "archiveCase.failed",
              defaultMessage: "Failed to archive the case. Please try again.",
            }),
        variant: "destructive",
      });
      setIsFinalizing(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage id="archiveCase" />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="casesList.archiveCaseAlertDialog.description"
              defaultMessage={
                '{caseName} will be archived, this action cannot be undone. Case will be moved to "Archived" in your account settings.'
              }
              values={{
                // TODO: add skeleton in case the data is not loaded yet
                caseName: <strong>{data?.name}</strong>,
              }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={isFinalizing}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button
            onClick={onArchiveCase}
            disabled={isFinalizing}
            aria-busy={isFinalizing}
          >
            {isFinalizing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <FormattedMessage id="archive" defaultMessage="Archive" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
